<template>
    <div class="wizard-step">
        <b-row>
            <b-col cols="12">
                <StepIndicator :current-step="currentStep" :config="config" class="mb-5" @show-terms="$emit('show-terms')" />
                <h4>Verify Information</h4>
                <p class="text-muted">Please verify and update your contact information as needed. This will be used for the agreement and future communications.</p>
            </b-col>
        </b-row>

        <!-- Basic Information -->
        <b-row class="mt-4">
            <b-col cols="12">
                <b-card>
                    <h5>Personal Information</h5>
                    <p class="text-muted mb-3">Please use your legal first and last name.</p>
                    <b-row class="mt-3">
                        <b-col cols="12" md="6">
                            <b-form-group label="First Name *">
                                <b-form-input required v-model="formData.first_name"/>
                            </b-form-group>
                        </b-col>
                        <b-col cols="12" md="6">
                            <b-form-group label="Last Name *">
                                <b-form-input required v-model="formData.last_name"/>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-card>
            </b-col>
        </b-row>

        <!-- Address Information -->
        <b-row class="mt-4">
            <b-col cols="12">
                <b-card>
                    <h5>Address Information</h5>
                    <p class="text-muted mb-3">We need to verify your mailing address for document delivery.</p>

                    <!-- Address Verification UI -->
                    <div v-if="!isAddressVerified && !isManualEntry">
                        <div class="d-flex justify-content-between align-items-start mb-3">
                            <label class="mb-0">Enter your address to find suggestions *</label>
                            <b-button
                                variant="outline-primary"
                                @click="selectManualAddress">
                                <i class="fas fa-keyboard mr-1"></i>Manual Entry
                            </b-button>
                        </div>
                        <b-form-group class="autocomplete-wrapper">
                            <b-form-input
                                ref="addressLine1"
                                required
                                v-model="formData.address"
                                @input="onAddressInput"
                                @keydown="handleAutocompleteKeydown"
                                placeholder="Start typing your address..."
                            />
                            <small class="form-text text-info mt-2">
                                <i class="fas fa-info-circle mr-1"></i>
                                <strong>Note:</strong> Do not include apartment, suite, or unit numbers. You'll be able to add that information after your street address is verified.
                            </small>
                            <!-- Debug info -->
                            <div v-if="showAutocomplete" class="mt-2 text-small text-muted">
                                Showing {{ autocompleteResults.length }} results
                            </div>
                            <!-- Autocomplete dropdown -->
                            <div v-if="showAutocomplete" class="autocomplete-dropdown">
                                <div
                                    v-for="(result, index) in autocompleteResults"
                                    :key="index"
                                    class="autocomplete-item"
                                    :class="{ selected: selectedAutocompleteIndex === index }"
                                    @click="selectAddress(index)"
                                >
                                    <div class="address-line">{{ result.preview.address }}</div>
                                    <div class="city-state-zip">{{ result.preview.city }}, {{ result.preview.pc }}</div>
                                </div>
                                <div
                                    class="autocomplete-item no-suggestion"
                                    :class="{ selected: selectedAutocompleteIndex === autocompleteResults.length }"
                                    @click="selectManualAddress"
                                >
                                    <div class="address-line">No valid suggestion found?</div>
                                    <div class="city-state-zip">Enter address manually</div>
                                </div>
                            </div>
                        </b-form-group>
                    </div>

                    <!-- Manual Address Entry -->
                    <div v-else-if="isManualEntry">
                        <div class="d-flex justify-content-between align-items-start mb-3">
                            <div>
                                <h6 class="mb-1">Manual Address Entry</h6>
                                <small class="text-muted">Enter your address details below</small>
                            </div>
                            <b-button
                                variant="outline-primary"
                                class="btn-sm d-md-none"
                                @click="resetToAutocomplete">
                                <i class="fas fa-search mr-1"></i>Autocomplete
                            </b-button>
                            <b-button
                                variant="outline-primary"
                                class="d-none d-md-inline-block"
                                @click="resetToAutocomplete">
                                <i class="fas fa-search mr-1"></i>Use Autocomplete
                            </b-button>
                        </div>
                        <b-row>
                            <b-col cols="12">
                                <b-form-group label="Address *">
                                    <b-form-input
                                        required
                                        v-model="formData.address"
                                        :readonly="isAddressVerified"
                                    />
                                </b-form-group>
                            </b-col>
                            <b-col cols="12">
                                <b-form-group label="Address Line 2 (Optional)">
                                    <b-form-input
                                        v-model="formData.address_2"
                                        :readonly="isAddressVerified"
                                    />
                                </b-form-group>
                            </b-col>
                            <b-col cols="12" md="6">
                                <b-form-group label="City *">
                                    <b-form-input
                                        required
                                        v-model="formData.city"
                                        :readonly="isAddressVerified"
                                    />
                                </b-form-group>
                            </b-col>
                            <b-col cols="6" md="3">
                                <b-form-group label="State *">
                                    <b-form-input
                                        required
                                        v-model="formData.state"
                                        :readonly="isAddressVerified"
                                    />
                                </b-form-group>
                            </b-col>
                            <b-col cols="6" md="3">
                                <b-form-group label="ZIP Code *">
                                    <b-form-input
                                        required
                                        v-model="formData.zip"
                                        :readonly="isAddressVerified"
                                    />
                                </b-form-group>
                            </b-col>
                        </b-row>

                        <!-- Address Validation Actions -->
                        <div v-if="isManualEntry" class="mt-3">
                            <b-button
                                variant="primary"
                                @click="validateAddress"
                                :disabled="!allRequiredAddressFields()"
                            >
                                <i class="fas fa-check mr-2"></i>Verify Address
                            </b-button>
                        </div>

                        <div v-if="isAddressVerified" class="mt-3">
                            <b-button variant="link" @click="resetAddress">
                                <i class="fas fa-edit mr-2"></i>Change Address
                            </b-button>
                        </div>
                    </div>

                    <!-- Verified Address (read-only state) -->
                    <div v-else>
                        <div class="mb-3">
                            <h6 class="mb-1">Verified Address</h6>
                            <small class="text-muted">You can add or edit the apartment/unit number below if needed.</small>
                        </div>

                        <b-row>
                            <b-col cols="12">
                                <b-form-group label="Address *">
                                    <b-form-input
                                        v-model="formData.address"
                                        disabled
                                    />
                                </b-form-group>
                            </b-col>
                            <b-col cols="12">
                                <b-form-group label="Address Line 2 (Apt, Suite, Unit, etc.)">
                                    <b-form-input
                                        v-model="formData.address_2"
                                        placeholder="Optional - Enter apartment, suite, or unit number"
                                    />
                                </b-form-group>
                            </b-col>
                            <b-col cols="12" md="6">
                                <b-form-group label="City *">
                                    <b-form-input
                                        v-model="formData.city"
                                        disabled
                                    />
                                </b-form-group>
                            </b-col>
                            <b-col cols="6" md="3">
                                <b-form-group label="State *">
                                    <b-form-input
                                        v-model="formData.state"
                                        disabled
                                    />
                                </b-form-group>
                            </b-col>
                            <b-col cols="6" md="3">
                                <b-form-group label="ZIP Code *">
                                    <b-form-input
                                        v-model="formData.zip"
                                        disabled
                                    />
                                </b-form-group>
                            </b-col>
                        </b-row>

                        <div class="mt-3">
                            <b-button
                                v-if="!addressValidation.postgridVerified"
                                variant="primary"
                                @click="reverifyAddressWithApartment"
                                class="mr-2">
                                <i class="fas fa-check mr-2"></i>Re-verify Address
                            </b-button>
                            <b-button variant="link" @click="resetAddress" class="pl-0">
                                <i class="fas fa-search mr-2"></i>Search for different address
                            </b-button>
                        </div>
                    </div>

                    <!-- Address Validation Status -->
                    <div v-if="addressValidation.message && addressValidation.isValid" class="mt-2">
                        <small class="text-success">
                            <i class="fas fa-check-circle mr-1"></i>
                            {{addressValidation.message}}
                        </small>
                    </div>
                    <div v-else-if="addressValidation.message && !addressValidation.isValid" class="mt-2">
                        <small class="text-warning">
                            <i class="fas fa-exclamation-triangle mr-1"></i>
                            {{addressValidation.message}}
                        </small>
                    </div>
                    <!-- Show bypass checkbox whenever address validation failed but can be bypassed -->
                    <div v-if="addressValidation.canBypass && !isAddressVerified" class="mt-2">
                        <b-form-checkbox v-model="addressBypass" size="sm">
                            <small>I understand there may be mail delivery issues and wish to proceed anyway</small>
                        </b-form-checkbox>
                    </div>
                </b-card>
            </b-col>
        </b-row>

        <!-- Email Information -->
        <b-row class="mt-4">
            <b-col cols="12">
                <b-card>
                    <h5>Email Information</h5>
                    <p class="text-muted mb-3">Please verify your email address(es) to ensure deliverability. We advise whitelisting our email address(es). If you don't see the verification code, please check your spam or junk mail folder.</p>

                    <b-row>
                        <!-- Primary Email -->
                        <b-col cols="12" md="6" class="mb-4 mb-md-0">
                            <b-form-group label="Primary Email Address *">
                                <b-form-input
                                    required
                                    type="email"
                                    v-model="formData.email"
                                    :readonly="emailVerification.primary.verified"
                                />
                            </b-form-group>

                            <!-- Primary Email Verification -->
                            <div v-if="!emailVerification.primary.verified">
                                <b-button
                                    variant="primary"
                                    @click="sendEmailVerification('primary')"
                                    :disabled="!formData.email || emailVerification.primary.requested"
                                >
                                    <i class="fas fa-envelope mr-2"></i>
                                    {{emailVerification.primary.requested ? 'Code Sent' : 'Verify Email'}}
                                </b-button>

                                <div v-if="emailVerification.primary.requested" class="mt-3">
                                    <b-form-group label="Enter email verification code">
                                        <VOtpInput
                                            ref="primaryEmailOtp"
                                            input-classes="otp-input"
                                            separator=""
                                            :num-inputs="6"
                                            :should-auto-focus="true"
                                            :is-input-num="true"
                                            @on-change="(value) => handleEmailOtpChange(value, 'primary')"
                                            @on-complete="(value) => handleEmailOtpComplete(value, 'primary')"
                                        />
                                    </b-form-group>
                                    <div class="mt-2">
                                        <b-button variant="success" @click="verifyEmailCode('primary')" :disabled="emailVerification.primary.code.length !== 6">
                                            <i class="fas fa-check mr-2"></i>Verify Code
                                        </b-button>
                                    </div>
                                    <div v-if="emailVerification.primary.error" class="text-danger mt-1">
                                        <small>Invalid code. Please try again.</small>
                                    </div>
                                </div>
                            </div>

                            <div v-if="emailVerification.primary.verified" class="mt-2">
                                <small class="text-success">
                                    <i class="fas fa-check-circle mr-1"></i>Email verified successfully
                                </small>
                            </div>
                        </b-col>

                        <!-- Secondary Email -->
                        <b-col cols="12" md="6" class="mb-4 mb-md-0">
                            <b-form-group label="Secondary Email Address">
                                <b-form-input
                                    type="email"
                                    v-model="formData.email_2"
                                    :readonly="emailVerification.secondary.verified"
                                    placeholder="Enter secondary email"
                                />
                            </b-form-group>

                            <!-- Secondary Email Verification -->
                            <div v-if="!emailVerification.secondary.verified && formData.email_2">
                                <b-button
                                    variant="primary"
                                    @click="sendEmailVerification('secondary')"
                                    :disabled="!formData.email_2 || emailVerification.secondary.requested"
                                >
                                    <i class="fas fa-envelope mr-2"></i>
                                    {{emailVerification.secondary.requested ? 'Code Sent' : 'Verify Email'}}
                                </b-button>

                                <div v-if="emailVerification.secondary.requested" class="mt-3">
                                    <b-form-group label="Enter email verification code">
                                        <VOtpInput
                                            ref="secondaryEmailOtp"
                                            input-classes="otp-input"
                                            separator=""
                                            :num-inputs="6"
                                            :should-auto-focus="true"
                                            :is-input-num="true"
                                            @on-change="(value) => handleEmailOtpChange(value, 'secondary')"
                                            @on-complete="(value) => handleEmailOtpComplete(value, 'secondary')"
                                        />
                                    </b-form-group>
                                    <div class="mt-2">
                                        <b-button variant="success" @click="verifyEmailCode('secondary')" :disabled="emailVerification.secondary.code.length !== 6">
                                            <i class="fas fa-check mr-2"></i>Verify Code
                                        </b-button>
                                    </div>
                                    <div v-if="emailVerification.secondary.error" class="text-danger mt-1">
                                        <small>Invalid code. Please try again.</small>
                                    </div>
                                </div>
                            </div>

                            <div v-if="emailVerification.secondary.verified" class="mt-2">
                                <small class="text-success">
                                    <i class="fas fa-check-circle mr-1"></i>Email verified successfully
                                </small>
                            </div>
                        </b-col>
                    </b-row>
                </b-card>
            </b-col>
        </b-row>

        <!-- Phone Information -->
        <b-row class="mt-4">
            <b-col cols="12">
                <b-card>
                    <h5>Phone Information</h5>
                    <p class="text-muted mb-3">We need your phone number for SMS notifications and account security.</p>

                    <b-row>
                        <!-- Primary Phone with SMS Verification -->
                        <b-col cols="12" md="6" class="mb-4 mb-md-0">
                            <b-form-group label="Primary Phone (SMS) *">
                                <b-form-input
                                    required
                                    v-model="sms.number"
                                    v-mask="'(###) ###-####'"
                                    :readonly="sms.verified"
                                />
                            </b-form-group>

                            <!-- SMS Verification -->
                            <div v-if="!sms.verified">
                                <b-button
                                    variant="primary"
                                    @click="sendSmsVerification"
                                    :disabled="!sms.number || sms.requested"
                                >
                                    <i class="fas fa-sms mr-2"></i>
                                    {{sms.requested ? 'Code Sent' : 'Verify SMS'}}
                                </b-button>

                                <div v-if="sms.requested" class="mt-3">
                                    <b-form-group label="Enter SMS verification code">
                                        <VOtpInput
                                            ref="otpInput"
                                            input-classes="otp-input"
                                            separator=""
                                            :num-inputs="5"
                                            :should-auto-focus="true"
                                            :is-input-num="true"
                                            @on-change="handleOnChange"
                                            @on-complete="handleOnComplete"
                                        />
                                    </b-form-group>
                                    <div class="mt-2">
                                        <b-button variant="success" @click="verifySms" :disabled="sms.code.length !== 5">
                                            <i class="fas fa-check mr-2"></i>Verify Code
                                        </b-button>
                                    </div>
                                    <div v-if="sms.error" class="text-danger mt-1">
                                        <small>Invalid code. Please try again.</small>
                                    </div>
                                </div>
                            </div>

                            <div v-if="sms.verified" class="mt-2">
                                <small class="text-success">
                                    <i class="fas fa-check-circle mr-1"></i>SMS verified successfully
                                </small>
                            </div>
                        </b-col>

                        <!-- Secondary Phone -->
                        <b-col cols="12" md="6" class="mb-4 mb-md-0">
                            <b-form-group label="Secondary Phone (Optional)">
                                <b-form-input v-model="formData.phone_2" v-mask="'(###) ###-####'"/>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-card>
            </b-col>
        </b-row>
    </div>
</template>

<script>
import {apiUrl} from '@/constants/config';
import axios from 'axios';
import VOtpInput from '@bachdgvn/vue-otp-input';
import StepIndicator from '@/components/sign-agreement/StepIndicator.vue';
import {postgridStreetQuery} from '@/utils/contact';

export default {
    name: 'InformationVerification',
    components: {
        VOtpInput,
        StepIndicator
    },
    props: {
        formData: {
            type: Object,
            required: true
        },
        apiPath: {
            type: String,
            required: true
        },
        currentStep: {
            type: Number,
            required: true
        },
        verifiedDataFromDb: {
            type: Object,
            default: () => ({})
        },
        config: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            postgridApiKey: window.location.hostname === 'localhost' ? 'test_sk_xu8qZj7UQksafFt5JTrWmx' : 'live_pk_dgaCoQ49WfT3o8oGjmTpRo',

            // Address verification data
            addressValidation: {
                isValid: null,
                canBypass: false,
                message: '',
                postgridVerified: false
            },
            addressBypass: false,
            autocompleteResults: [],
            showAutocomplete: false,
            selectedAutocompleteIndex: -1,
            isAddressVerified: false,
            isManualEntry: false,

            // Email verification data
            emailVerification: {
                primary: {
                    requested: false,
                    code: '',
                    verified: false,
                    error: false,
                    expectedCode: ''
                },
                secondary: {
                    requested: false,
                    code: '',
                    verified: false,
                    error: false,
                    expectedCode: ''
                }
            },

            // SMS verification data
            sms: {
                number: '',
                requested: false,
                code: '',
                verified: false,
                error: false,
                expectedCode: ''
            }
        };
    },
    computed: {
        allRequiredFieldsFilled() {
            return this.formData.first_name &&
                   this.formData.last_name &&
                   this.formData.email &&
                   this.formData.email_2 &&
                   this.formData.address &&
                   this.formData.city &&
                   this.formData.state &&
                   this.formData.zip &&
                   this.sms.number;
        },
        allVerificationsComplete() {
            return (this.addressValidation.isValid === null ||
                    this.addressValidation.isValid ||
                    (this.addressValidation.canBypass && this.addressBypass)) &&
                   this.emailVerification.primary.verified &&
                   this.emailVerification.secondary.verified &&
                   this.sms.verified;
        }
    },
    methods: {
        // Address verification methods
        allRequiredAddressFields() {
            return this.formData.address &&
                   this.formData.city &&
                   this.formData.state &&
                   this.formData.zip;
        },

        async onAddressInput(value) {
            // Bootstrap Vue passes the value directly, not an event object
            const query = (value || '').trim();

            if (query.length >= 3) {
                await this.fetchAddressCompletions(query);
            } else {
                this.hideAutocomplete();
            }
        },

        async fetchAddressCompletions(partialStreet) {
            try {
                console.log('Fetching address completions for:', partialStreet);
                const url = `https://api.postgrid.com/v1/addver/completions?partialStreet=${encodeURIComponent(postgridStreetQuery(partialStreet))}`;
                const response = await fetch(url, {
                    headers: {
                        'x-api-key': this.postgridApiKey,
                        'Content-Type': 'application/json'
                    }
                });

                if (response.ok) {
                    const data = await response.json();
                    console.log('PostGrid completions response:', data);

                    if (data.data && data.data.length > 0) {
                        this.autocompleteResults = data.data;
                        this.showAutocomplete = true;
                        this.selectedAutocompleteIndex = 0;
                        console.log('Showing autocomplete with results:', this.autocompleteResults);
                    } else {
                        console.log('No results found');
                        this.hideAutocomplete();
                    }
                } else {
                    console.error('PostGrid API error:', response.status, response.statusText);
                    this.hideAutocomplete();
                }
            } catch (error) {
                console.error('Error fetching PostGrid completions:', error);
                this.hideAutocomplete();
            }
        },

        async selectAddress(index) {
            try {
                const response = await fetch(`https://api.postgrid.com/v1/addver/completions?index=${index}`, {
                    method: 'POST',
                    headers: {
                        'x-api-key': this.postgridApiKey,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        partialStreet: postgridStreetQuery(this.formData.address)
                    })
                });

                if (response.ok) {
                    const data = await response.json();
                    let addressData = null;

                    if (data.data) {
                        if (Array.isArray(data.data) && data.data.length > 0 && data.data[0].address) {
                            addressData = data.data[0].address;
                        } else if (data.data.address) {
                            addressData = data.data.address;
                        }
                    }

                    if (addressData) {
                        this.populateAddressFields(addressData);
                        // Verify the address with PostGrid verification API
                        this.$nextTick(async () => {
                            await this.verifyAddressFromAutocomplete();
                        });
                    }
                }
            } catch (error) {
                console.error('Error selecting PostGrid address:', error);
            }
            this.hideAutocomplete();
        },

        populateAddressFields(addressData) {
            this.formData.address = addressData.address || '';
            this.formData.address_2 = addressData.address2 || '';
            this.formData.city = addressData.city || '';
            this.formData.state = addressData.prov || '';
            this.formData.zip = addressData.pc || '';
        },

        async verifyWithFormData() {
            if (!this.allRequiredAddressFields()) {
                this.addressValidation = {
                    isValid: false,
                    canBypass: true,
                    message: 'Incomplete address information. Please try manual entry.',
                    postgridVerified: false
                };
                return;
            }

            try {
                const response = await fetch('https://api.postgrid.com/v1/addver/verifications', {
                    method: 'POST',
                    headers: {
                        'x-api-key': this.postgridApiKey,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        address: `${this.formData.address}${this.formData.address_2 ? ', ' + this.formData.address_2 : ''}, ${this.formData.city}, ${this.formData.state} ${this.formData.zip}`
                    })
                });

                if (response.ok) {
                    const verificationData = await response.json();
                    console.log('Address verification response:', verificationData);

                    if (verificationData.data) {
                        const verification = verificationData.data;

                        if (verification.status === 'verified') {
                            this.isAddressVerified = true;
                            this.hideAutocomplete(); // Hide any active autocomplete
                            this.addressValidation = {
                                isValid: true,
                                canBypass: false,
                                message: 'Address verified and deliverable',
                                postgridVerified: true
                            };

                            // Emit audit event
                            this.$emit('audit-event', {
                                eventType: 'address_verified',
                                data: { method: 'postgrid', status: 'verified', city: this.formData.city, state: this.formData.state }
                            });

                            // Immediately save verified address to database
                            this.saveVerifiedDataToDb();
                        } else if (verification.status === 'corrected') {
                            // Update with corrected address
                            this.populateAddressFields({
                                address: verification.line1,
                                address2: verification.line2 || '',
                                city: verification.city,
                                prov: verification.provinceOrState,
                                pc: verification.postalOrZip
                            });

                            // Show any correction details if there were errors
                            let message = 'Address corrected and verified';
                            if (verification.errors && Object.keys(verification.errors).length > 0) {
                                const errorDetails = Object.entries(verification.errors)
                                    .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
                                    .join('; ');
                                message += ` (Issues resolved: ${errorDetails})`;
                            }

                            // CRITICAL: Only set isAddressVerified = true for verified state
                            this.isAddressVerified = true;
                            this.hideAutocomplete(); // Hide any active autocomplete
                            this.addressValidation = {
                                isValid: true,
                                canBypass: false,
                                message: message,
                                postgridVerified: true
                            };

                            // Emit audit event
                            this.$emit('audit-event', {
                                eventType: 'address_verified',
                                data: { method: 'postgrid', status: 'corrected', city: this.formData.city, state: this.formData.state }
                            });

                            // Immediately save verified address to database
                            this.saveVerifiedDataToDb();
                        } else if (verification.status === 'failed') {
                            // Check if PostGrid returned a corrected address even though it failed
                            if (verification.line1) {
                                // Update with the corrected format from PostGrid
                                this.populateAddressFields({
                                    address: verification.line1,
                                    address2: verification.line2 || '',
                                    city: verification.city,
                                    prov: verification.provinceOrState,
                                    pc: verification.postalOrZip
                                });

                                // Build error message
                                let errorMessage = 'Address verification failed';
                                if (verification.errors && Object.keys(verification.errors).length > 0) {
                                    const errorDetails = Object.entries(verification.errors)
                                        .map(([field, messages]) => messages.join(', '))
                                        .join('; ');
                                    errorMessage += `: ${errorDetails}`;
                                }

                                this.addressValidation = {
                                    isValid: false,
                                    canBypass: true,
                                    message: errorMessage + '. Please correct the address or proceed anyway if you believe it is correct.',
                                    postgridVerified: false
                                };
                            } else {
                                this.addressValidation = {
                                    isValid: false,
                                    canBypass: true,
                                    message: 'Could not verify this address. Please check and try again, or proceed anyway.',
                                    postgridVerified: false
                                };
                            }
                        }
                    }
                } else {
                    console.error('Address verification error:', response.status, response.statusText);
                    this.addressValidation = {
                        isValid: true,
                        canBypass: false,
                        message: 'Address selected from autocomplete',
                        postgridVerified: true
                    };
                }
            } catch (error) {
                console.error('Error verifying address:', error);
                this.addressValidation = {
                    isValid: true,
                    canBypass: false,
                    message: 'Address selected from autocomplete',
                    postgridVerified: true
                };
            }
        },

        selectManualAddress() {
            this.isManualEntry = true;
            this.addressValidation = {
                isValid: null,
                canBypass: false,
                message: '',
                postgridVerified: false
            };
            this.hideAutocomplete();
        },

        async validateAddress() {
            await this.verifyWithFormData();
        },

        async verifyAddressFromAutocomplete() {
            if (!this.allRequiredAddressFields()) {
                return;
            }

            try {
                const response = await fetch('https://api.postgrid.com/v1/addver/verifications', {
                    method: 'POST',
                    headers: {
                        'x-api-key': this.postgridApiKey,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        address: `${this.formData.address}${this.formData.address_2 ? ', ' + this.formData.address_2 : ''}, ${this.formData.city}, ${this.formData.state} ${this.formData.zip}`
                    })
                });

                if (response.ok) {
                    const verificationData = await response.json();
                    console.log('Address verification response (from autocomplete):', verificationData);

                    if (verificationData.data) {
                        const verification = verificationData.data;

                        if (verification.status === 'verified') {
                            this.isAddressVerified = true;
                            this.addressValidation = {
                                isValid: true,
                                canBypass: false,
                                message: 'Address verified and deliverable',
                                postgridVerified: true
                            };

                            // Immediately save verified address to database
                            this.saveVerifiedDataToDb();
                        } else if (verification.status === 'corrected') {
                            // Update with corrected address
                            this.populateAddressFields({
                                address: verification.line1,
                                address2: verification.line2 || '',
                                city: verification.city,
                                prov: verification.provinceOrState,
                                pc: verification.postalOrZip
                            });

                            this.isAddressVerified = true;
                            this.addressValidation = {
                                isValid: true,
                                canBypass: false,
                                message: 'Address corrected and verified',
                                postgridVerified: true
                            };

                            // Immediately save verified address to database
                            this.saveVerifiedDataToDb();
                        } else if (verification.status === 'failed') {
                            // Address from autocomplete failed PostGrid verification
                            // Switch to manual entry so user can edit/add apartment and re-verify
                            // (with bypass available if it still fails)
                            this.isManualEntry = true;
                            this.isAddressVerified = false;
                            this.addressValidation = {
                                isValid: false,
                                canBypass: true,
                                message: 'Address could not be fully verified. Please add apartment/unit if applicable and verify, or proceed anyway.',
                                postgridVerified: false
                            };

                            // Show a toast to guide the user
                            this.$bvToast.toast(
                                'The selected address could not be verified. Please review, add apartment/unit if needed, and click "Verify Address".',
                                {
                                    title: 'Address Verification Needed',
                                    variant: 'warning',
                                    autoHideDelay: 6000,
                                    solid: true
                                }
                            );
                        }
                    }
                } else {
                    // API error - trust the autocomplete selection
                    this.isAddressVerified = true;
                    this.addressValidation = {
                        isValid: true,
                        canBypass: false,
                        message: 'Address selected from autocomplete',
                        postgridVerified: true
                    };
                }
            } catch (error) {
                console.error('Error verifying address from autocomplete:', error);
                // Error - trust the autocomplete selection
                this.isAddressVerified = true;
                this.addressValidation = {
                    isValid: true,
                    canBypass: false,
                    message: 'Address selected from autocomplete',
                    postgridVerified: true
                };
            }
        },

        resetAddress() {
            this.formData.address = '';
            this.formData.address_2 = '';
            this.formData.city = '';
            this.formData.state = '';
            this.formData.zip = '';
            this.isAddressVerified = false;
            this.isManualEntry = false;
            this.addressValidation = {
                isValid: null,
                canBypass: false,
                message: '',
                postgridVerified: false
            };
            // Focus the address input after reset
            this.$nextTick(() => {
                if (this.$refs.addressLine1) {
                    this.$refs.addressLine1.focus();
                }
            });
        },

        async reverifyAddressWithApartment() {
            // Re-verify the address with the apartment number included
            await this.verifyAddressFromAutocomplete();
        },

        hideAutocomplete() {
            setTimeout(() => {
                this.showAutocomplete = false;
                this.autocompleteResults = [];
                this.selectedAutocompleteIndex = -1;
            }, 150);
        },

        handleAutocompleteKeydown(event) {
            if (!this.showAutocomplete) return;
            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    this.selectedAutocompleteIndex = Math.min(
                        this.selectedAutocompleteIndex + 1,
                        this.autocompleteResults.length - 1
                    );
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    this.selectedAutocompleteIndex = Math.max(this.selectedAutocompleteIndex - 1, 0);
                    break;
                case 'Enter':
                    event.preventDefault();
                    if (this.selectedAutocompleteIndex >= 0) {
                        this.selectAddress(this.selectedAutocompleteIndex);
                    }
                    break;
                case 'Escape':
                    this.hideAutocomplete();
                    break;
            }
        },

        // Email verification methods
        async sendEmailVerification(type) {
            const email = type === 'primary' ? this.formData.email : this.formData.email_2;
            try {
                this.emailVerification[type].error = false;
                this.emailVerification[type].requested = true;
                this.emailVerification[type].code = '';

                // Emit audit event
                this.$emit('audit-event', {
                    eventType: 'email_verification_sent',
                    data: { emailType: type, emailMasked: email.replace(/(.{2})(.*)(@.*)/, '$1***$3') }
                });

                // Call email verification API
                const response = await axios.post(`${apiUrl}${this.apiPath}/send-email-verification`, {
                    email: email,
                    type: type
                });

                if (response.data.code) {
                    this.emailVerification[type].expectedCode = response.data.code;
                    console.log(`Email verification code for ${email}: ${response.data.code}`);
                }
            } catch (error) {
                console.error('Error sending email verification:', error);
                this.emailVerification[type].requested = false;
                this.emailVerification[type].error = true;
            }
        },

        handleEmailOtpChange(value, type) {
            this.emailVerification[type].code = value;
        },

        handleEmailOtpComplete(value, type) {
            this.emailVerification[type].code = value;
        },

        async verifyEmailCode(type) {
            this.emailVerification[type].error = false;
            if (this.emailVerification[type].code === this.emailVerification[type].expectedCode) {
                this.emailVerification[type].verified = true;
                this.emailVerification[type].requested = false;
                this.emailVerification[type].error = false;

                // Emit audit event for successful verification
                this.$emit('audit-event', {
                    eventType: 'email_verified',
                    data: { emailType: type }
                });

                // Immediately save verified email to database
                this.saveVerifiedDataToDb();
            } else {
                this.emailVerification[type].error = true;
                // Clear the OTP input
                if (type === 'primary' && this.$refs.primaryEmailOtp) {
                    this.$refs.primaryEmailOtp.clearInput();
                } else if (type === 'secondary' && this.$refs.secondaryEmailOtp) {
                    this.$refs.secondaryEmailOtp.clearInput();
                }
                this.emailVerification[type].code = '';
            }
        },

        // Deprecated - kept for compatibility if needed
        async verifyEmail(type) {
            this.verifyEmailCode(type);
        },

        // SMS verification methods
        async sendSmsVerification() {
            this.sms.error = false;
            this.sms.requested = true;
            this.sms.code = '';

            // Emit audit event
            this.$emit('audit-event', {
                eventType: 'sms_verification_sent',
                data: { phoneMasked: this.sms.number.replace(/\d(?=\d{4})/g, '*') }
            });

            try {
                // Copy the SMS number to formData.phone for consistency
                this.formData.phone = this.sms.number;

                // Call SMS verification API
                const response = await axios.post(`${apiUrl}${this.apiPath}/send-sms-verification`, {
                    number: this.sms.number.replace(/\D/g, ''),
                    email: this.formData.email
                });

                if (response.data.code) {
                    this.sms.expectedCode = response.data.code;
                    console.log(`SMS verification code for ${this.sms.number}: ${response.data.code}`);
                }
            } catch (error) {
                console.error('Error sending SMS verification:', error);
                this.sms.requested = false;
                this.sms.error = true;
            }
        },

        async verifySms() {
            this.sms.error = false;
            if (this.sms.code === this.sms.expectedCode) {
                this.sms.verified = true;
                this.sms.requested = false;
                this.sms.error = false;

                // Emit audit event for successful verification
                this.$emit('audit-event', {
                    eventType: 'sms_verified',
                    data: {}
                });

                // Immediately save verified SMS to database
                this.saveVerifiedDataToDb();
            } else {
                this.sms.error = true;
                if (this.$refs.otpInput) {
                    this.$refs.otpInput.clearInput();
                }
                this.sms.code = '';
            }
        },

        handleOnComplete(value) {
            this.sms.code = value;
        },

        handleOnChange(value) {
            this.sms.code = value;
        },

        // Helper method to format city/state/zip display from PostGrid results
        formatCityStateZip(result) {
            if (!result) return '';

            // Try different possible field names from PostGrid API
            const city = result.city || result.locality || '';
            const state = result.prov || result.province || result.state || result.provinceOrState || '';
            const zip = result.pc || result.postalCode || result.zip || result.postalOrZip || '';

            const parts = [city, state, zip].filter(part => part && part.trim());
            return parts.join(', ');
        },

        // Initial address verification on component load
        async verifyInitialAddress() {
            // Check if user already has a complete address on file
            if (this.formData.address && this.formData.city && this.formData.state && this.formData.zip) {
                console.log('Verifying existing address on file with PostGrid...');

                // Verify the address on file with PostGrid
                await this.verifyWithFormData();

                // If verification failed, clear the address and force user to use autocomplete
                if (!this.isAddressVerified) {
                    console.log('Address verification failed. Clearing address and requiring re-entry.');
                    this.resetToAutocomplete();

                    // Show a toast notification to inform the user
                    this.$bvToast.toast(
                        'The address we have on file could not be verified. Please search for and select your address from the suggestions.',
                        {
                            title: 'Address Verification Required',
                            variant: 'warning',
                            autoHideDelay: 8000,
                            solid: true
                        }
                    );
                }
            } else {
                // No complete address on file - show single input field
                this.resetToAutocomplete();
            }
        },

        resetToAutocomplete() {
            // Clear address fields and show single input
            this.formData.address = '';
            this.formData.address_2 = '';
            this.formData.city = '';
            this.formData.state = '';
            this.formData.zip = '';
            this.isAddressVerified = false;
            this.isManualEntry = false;
            this.addressValidation = {
                isValid: null,
                canBypass: false,
                message: '',
                postgridVerified: false
            };
        },

        // Export verification data for parent component
        getVerificationData() {
            // Include address if PostGrid verified OR if user bypassed verification
            const addressAccepted = this.isAddressVerified || (this.addressValidation.canBypass && this.addressBypass);

            return {
                verified_email: this.emailVerification.primary.verified ? this.formData.email : null,
                verified_email_alt: this.emailVerification.secondary.verified && this.formData.email_2 ? this.formData.email_2 : null,
                verified_sms: this.sms.verified ? this.sms.number.replace(/\D/g, '') : null,
                verified_mailing_address: addressAccepted ? JSON.stringify({
                    address: this.formData.address,
                    address_2: this.formData.address_2,
                    city: this.formData.city,
                    state: this.formData.state,
                    zip: this.formData.zip
                }) : null,
                postgrid_verified: this.addressValidation.postgridVerified,
                postgrid_address_bypass: this.addressBypass
            };
        },

        // Load verified data from database and pre-populate verification state
        loadVerifiedDataFromDb() {
            if (!this.verifiedDataFromDb) return;

            // Check and load verified primary email
            if (this.verifiedDataFromDb.verified_email && this.verifiedDataFromDb.verified_email === this.formData.email) {
                this.emailVerification.primary.verified = true;
                console.log('Loaded verified primary email from database');
            }

            // Check and load verified secondary email
            if (this.verifiedDataFromDb.verified_email_alt && this.verifiedDataFromDb.verified_email_alt === this.formData.email_2) {
                this.emailVerification.secondary.verified = true;
                console.log('Loaded verified secondary email from database');
            }

            // Check and load verified SMS
            if (this.verifiedDataFromDb.verified_sms) {
                // Format the verified SMS for display
                const verifiedSmsFormatted = this.verifiedDataFromDb.verified_sms.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
                const currentSmsDigitsOnly = this.sms.number.replace(/\D/g, '');

                // Check if current SMS matches verified SMS
                if (this.verifiedDataFromDb.verified_sms === currentSmsDigitsOnly) {
                    this.sms.verified = true;
                    console.log('Loaded verified SMS from database');
                }
            }

            // Check and load verified address — only restore if it was actually PostGrid verified or bypassed
            if (this.verifiedDataFromDb.verified_mailing_address &&
                (this.verifiedDataFromDb.postgrid_verified || this.verifiedDataFromDb.postgrid_address_bypass)) {
                try {
                    const verifiedAddress = JSON.parse(this.verifiedDataFromDb.verified_mailing_address);

                    if (verifiedAddress.address && verifiedAddress.city && verifiedAddress.state && verifiedAddress.zip) {
                        this.formData.address = verifiedAddress.address;
                        this.formData.address_2 = verifiedAddress.address_2 || '';
                        this.formData.city = verifiedAddress.city;
                        this.formData.state = verifiedAddress.state;
                        this.formData.zip = verifiedAddress.zip;

                        this.isAddressVerified = true;
                        this.addressValidation = {
                            isValid: true,
                            canBypass: false,
                            message: this.verifiedDataFromDb.postgrid_verified
                                ? 'Address previously verified'
                                : 'Address previously accepted (verification bypassed)',
                            postgridVerified: !!this.verifiedDataFromDb.postgrid_verified
                        };
                        console.log('Loaded verified address from database and populated form');
                    }
                } catch (e) {
                    console.error('Error parsing verified_mailing_address:', e);
                }
            }
        },

        // Save verified data to database immediately after verification
        async saveVerifiedDataToDb() {
            try {
                const verificationData = this.getVerificationData();

                const response = await axios.post(`${apiUrl}${this.apiPath}/save-verified-data`, verificationData);

                if (response.data.success) {
                    console.log('Verified contact data saved to database:', verificationData);
                } else {
                    console.warn('Failed to save verified data:', response.data);
                }
            } catch (error) {
                console.error('Error saving verified contact data:', error);
                // Don't show error to user - this is a background save operation
                // The data will still be sent on final submission
            }
        }
    },
    mounted() {
        // Emit step entered event for audit
        this.$emit('audit-event', {
            eventType: 'step_2_entered',
            data: { stepName: 'information_verification' }
        });

        // Initialize SMS number from formData if available
        if (this.formData.phone) {
            this.sms.number = this.formData.phone;
        }

        // Load persistent verification state from database
        this.loadVerifiedDataFromDb();

        // Component is only mounted when currentStep === 2, so it's safe to verify now
        // Skip PostGrid re-verification if address was already restored from a previous session
        if (this.isAddressVerified) {
            console.log('Address already verified from previous session, skipping PostGrid re-verification');
        } else if (this.formData.address && this.formData.city && this.formData.state && this.formData.zip) {
            this.verifyInitialAddress();
        } else {
            // No complete address - show autocomplete input
            this.resetToAutocomplete();
        }
    },
    computed: {
        allVerificationsComplete() {
            // Secondary email is optional - if provided, it should be verified
            const secondaryEmailValid = !this.formData.email_2 || this.emailVerification.secondary.verified;

            // Address is valid if: verified, or failed but bypassed
            const addressValid = this.isAddressVerified ||
                                (this.addressValidation.canBypass && this.addressBypass);

            return addressValid &&
                   this.emailVerification.primary.verified &&
                   secondaryEmailValid &&
                   this.sms.verified;
        }
    },
    watch: {
        allVerificationsComplete: {
            handler(complete) {
                // Emit validation status to parent component
                this.$emit('validation-changed', complete);

                // Also emit verification data so parent can send it to backend
                this.$emit('verification-data-changed', this.getVerificationData());
            },
            immediate: true
        },
        'emailVerification.primary.verified'() {
            this.$emit('verification-data-changed', this.getVerificationData());
        },
        'emailVerification.secondary.verified'() {
            this.$emit('verification-data-changed', this.getVerificationData());
        },
        'sms.verified'() {
            this.$emit('verification-data-changed', this.getVerificationData());
        },
        'addressValidation.postgridVerified'() {
            this.$emit('verification-data-changed', this.getVerificationData());
        },
        addressBypass(bypassed) {
            this.$emit('verification-data-changed', this.getVerificationData());
            if (bypassed) {
                // Save immediately when user bypasses address verification
                this.saveVerifiedDataToDb();
            }
        }
    }
};
</script>

<style lang="scss" scoped>
    @import '~@/assets/scss/_variables.scss';

    .card {
        border: 1px solid #e9ecef;
        box-shadow: 0 2px 4px rgba(0,0,0,0.05);

        h5 {
            color: #464577;
            margin-bottom: 15px;
        }
    }

    /* Manual Autocomplete Styles */
    .autocomplete-wrapper {
        position: relative;
    }

    .autocomplete-dropdown {
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background: white;
        border: 1px solid #ddd;
        border-radius: 4px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        max-height: 300px;
        overflow-y: auto;
        z-index: 9999 !important;
        margin-top: 2px;
        min-width: 100%;
    }

    .autocomplete-item {
        padding: 12px 16px;
        cursor: pointer;
        transition: background-color 0.2s;
        border-bottom: 1px solid #f0f0f0;

        &:last-child {
            border-bottom: none;
        }

        &:hover,
        &.selected {
            background-color: #f8f9fa;
        }

        &.selected {
            background-color: #e3f2fd;
        }

        .address-line {
            font-weight: 500;
            color: #333;
            font-size: 0.95rem;
        }

        .city-state-zip {
            color: #666;
            font-size: 0.85rem;
            margin-top: 2px;
        }

        &.no-suggestion {
            border-top: 1px solid #ddd;
            background-color: #f8f9fa;
            font-style: italic;

            &:hover,
            &.selected {
                background-color: #e3f2fd;
            }

            .address-line {
                color: #495057;
                font-weight: normal;
            }

            .city-state-zip {
                color: #6c757d;
            }
        }
    }
</style>

<style lang="scss">
    .otp-input {
        width: 40px;
        height: 40px;
        padding: 5px;
        margin: 0 10px;
        font-size: 20px;
        border-radius: 4px;
        border: 1px solid rgba(0, 0, 0, 0.3);
        text-align: center;

        &.error {
            border: 1px solid red !important;
        }
    }

    .otp-input::-webkit-inner-spin-button,
    .otp-input::-webkit-outer-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }
</style>